import { useEffect, useState } from 'react';

import { confirmRiskNotification } from './api/confirmRiskNotification.js';
import RiskNoticePage from './components/RiskNoticePage.jsx';
import { getLocale } from './locales/content.js';
import { validateIinBin } from './utils/validateIinBin.js';

const INITIAL_FORM = {
  fullName: '',
  iinBin: '',
};

const INITIAL_FIELD_ERRORS = {
  fullName: '',
  iinBin: '',
};

function getFieldErrors(form, errors) {
  const nextErrors = { ...INITIAL_FIELD_ERRORS };

  if (!form.fullName.trim()) {
    nextErrors.fullName = errors.fullName;
  }

  const iinBin = form.iinBin.trim();

  if (!iinBin) {
    nextErrors.iinBin = errors.iinBinRequired;
  } else if (!/^\d{12}$/.test(iinBin)) {
    nextErrors.iinBin = errors.iinBinLength;
  } else if (!validateIinBin(iinBin)) {
    nextErrors.iinBin = errors.iinBinInvalid;
  }

  return nextErrors;
}

function App() {
  const [language, setLanguage] = useState('ru');
  const [form, setForm] = useState(INITIAL_FORM);
  const [fieldErrors, setFieldErrors] = useState(INITIAL_FIELD_ERRORS);
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const locale = getLocale(language);

  useEffect(() => {
    document.documentElement.lang = language;
    document.title = locale.title;
  }, [language, locale.title]);

  const handleChange = (field) => (event) => {
    let value = event.target.value;

    if (field === 'iinBin') {
      value = value.replace(/\D/g, '').slice(0, 12);
    }

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }

    if (status === 'error') {
      setStatus('idle');
      setErrorMessage('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextFieldErrors = getFieldErrors(form, locale.errors);
    const hasFieldErrors = Object.values(nextFieldErrors).some(Boolean);

    if (hasFieldErrors) {
      setFieldErrors(nextFieldErrors);
      setStatus('error');
      setErrorMessage('');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      await confirmRiskNotification({
        fullName: form.fullName.trim(),
        identificationNumber: form.iinBin.trim(),
        language,
      });
      setForm(INITIAL_FORM);
      setFieldErrors(INITIAL_FIELD_ERRORS);
      setStatus('success');
    } catch (error) {
      setStatus('error');
      setErrorMessage(
        error instanceof Error ? error.message : locale.errors.submitFailed
      );
    }
  };

  const handleCloseSuccess = () => {
    setStatus('idle');
  };

  return (
    <RiskNoticePage
      form={form}
      fieldErrors={fieldErrors}
      status={status}
      errorMessage={errorMessage}
      locale={locale}
      language={language}
      onLanguageChange={setLanguage}
      onChange={handleChange}
      onSubmit={handleSubmit}
      onCloseSuccess={handleCloseSuccess}
    />
  );
}

export default App;
